import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, FileText } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import IncomeParametersSection from './IncomeParametersSection';
import ExpenseParametersSection from './ExpenseParametersSection';
import TaxRateParametersSection from './TaxRateParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import SpecialDeductionsSection from './SpecialDeductionsSection';
import DividendsSection from './DividendsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета налога на прибыль
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  calculations,
  setCalculations,
  taxRegions,
  specialZoneTypes,
  advancePaymentMethods,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обработка зависимых полей
      if (name === 'specialDeductions' && !checked) {
        newState.researchAndDevelopment = '0';
        newState.investmentDeduction = '0';
        newState.lossesCarriedForward = '0';
      }
      
      if (name === 'specialTaxRegimes' && !checked) {
        newState.isITCompany = false;
        newState.isResidentSpecialZone = false;
        newState.specialZoneType = 'none';
      }
      
      if (name === 'isResidentSpecialZone' && !checked) {
        newState.specialZoneType = 'none';
      }
      
      if (name === 'dividends' && !checked) {
        newState.dividendsIncome = '0';
        newState.dividendsExpenses = '0';
        newState.participantType = 'resident';
        newState.participationShare = '100';
      }

      if (name === 'advancePayments' && !checked) {
        newState.advancePaymentMethod = 'quarter';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      revenue, otherIncome, expensesRelated, expensesOther,
      taxationYear, taxRegion,
      specialDeductions, researchAndDevelopment, investmentDeduction, lossesCarriedForward,
      dividends, dividendsIncome, dividendsExpenses, participationShare
    } = formData;

    // Парсинг числовых значений
    const numRevenue = parseFloatOrDefault(revenue, -1);
    const numOtherIncome = parseFloatOrDefault(otherIncome, 0);
    const numExpensesRelated = parseFloatOrDefault(expensesRelated, 0);
    const numExpensesOther = parseFloatOrDefault(expensesOther, 0);

    // Валидация базовых полей доходов и расходов
    if (numRevenue < 0) newErrors.revenue = 'Выручка не может быть отрицательной.';
    if (numOtherIncome < 0) newErrors.otherIncome = 'Внереализационные доходы не могут быть отрицательными.';
    if (numExpensesRelated < 0) newErrors.expensesRelated = 'Расходы не могут быть отрицательными.';
    if (numExpensesOther < 0) newErrors.expensesOther = 'Внереализационные расходы не могут быть отрицательными.';

    // Валидация налогового периода
    const currentYear = new Date().getFullYear();
    const taxYear = parseIntOrDefault(taxationYear, 0);
    if (!taxYear || taxYear < 2000 || taxYear > currentYear + 1) {
      newErrors.taxationYear = `Укажите корректный год (2000-${currentYear + 1}).`;
    }

    // Валидация специальных вычетов и льгот
    if (specialDeductions) {
      const numRD = parseFloatOrDefault(researchAndDevelopment, 0);
      const numInvestmentDeduction = parseFloatOrDefault(investmentDeduction, 0);
      const numLossesCarriedForward = parseFloatOrDefault(lossesCarriedForward, 0);

      if (numRD < 0) newErrors.researchAndDevelopment = 'Расходы на НИОКР не могут быть отрицательными.';
      if (numInvestmentDeduction < 0) newErrors.investmentDeduction = 'Инвестиционный вычет не может быть отрицательным.';
      if (numLossesCarriedForward < 0) newErrors.lossesCarriedForward = 'Убытки прошлых лет не могут быть отрицательными.';
      
      // Проверка, чтобы убытки не превышали доходов
      const totalIncome = numRevenue + numOtherIncome;
      const totalBaseExpenses = numExpensesRelated + numExpensesOther;
      
      if (numLossesCarriedForward > totalIncome) {
        newErrors.lossesCarriedForward = 'Убытки прошлых лет не могут превышать сумму всех доходов.';
      }
      
      if (numRD > numExpensesRelated) {
        newErrors.researchAndDevelopment = 'Расходы на НИОКР не могут превышать общие расходы, связанные с производством.';
      }
    }

    // Валидация дивидендов
    if (dividends) {
      const numDividendsIncome = parseFloatOrDefault(dividendsIncome, 0);
      const numDividendsExpenses = parseFloatOrDefault(dividendsExpenses, 0);
      const numParticipationShare = parseFloatOrDefault(participationShare, 0);

      if (numDividendsIncome < 0) newErrors.dividendsIncome = 'Доходы от дивидендов не могут быть отрицательными.';
      if (numDividendsExpenses < 0) newErrors.dividendsExpenses = 'Расходы на дивиденды не могут быть отрицательными.';
      
      if (numParticipationShare <= 0 || numParticipationShare > 100) {
        newErrors.participationShare = 'Доля участия должна быть в диапазоне от 1% до 100%.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Основная функция расчета
  const calculateTax = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setCalculations(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numRevenue = parseFloatOrDefault(formData.revenue, 0);
        const numOtherIncome = parseFloatOrDefault(formData.otherIncome, 0);
        const numExpensesRelated = parseFloatOrDefault(formData.expensesRelated, 0);
        const numExpensesOther = parseFloatOrDefault(formData.expensesOther, 0);
        
        // Специальные вычеты
        const useSpecialDeductions = formData.specialDeductions;
        const numResearchAndDevelopment = useSpecialDeductions ? parseFloatOrDefault(formData.researchAndDevelopment, 0) : 0;
        const numInvestmentDeduction = useSpecialDeductions ? parseFloatOrDefault(formData.investmentDeduction, 0) : 0;
        const numLossesCarriedForward = useSpecialDeductions ? parseFloatOrDefault(formData.lossesCarriedForward, 0) : 0;
        
        // Дивиденды
        const includeDividends = formData.dividends;
        const numDividendsIncome = includeDividends ? parseFloatOrDefault(formData.dividendsIncome, 0) : 0;
        const numDividendsExpenses = includeDividends ? parseFloatOrDefault(formData.dividendsExpenses, 0) : 0;
        const participantType = formData.participantType;
        const participationShare = parseFloatOrDefault(formData.participationShare, 100);
        
        // Специальные налоговые режимы
        const useSpecialTaxRegimes = formData.specialTaxRegimes;
        const isITCompany = useSpecialTaxRegimes && formData.isITCompany;
        const isResidentSpecialZone = useSpecialTaxRegimes && formData.isResidentSpecialZone;
        const specialZoneType = formData.specialZoneType;
        
        // Определение налоговых ставок
        const federalRate = 3; // Федеральная часть (3%)
        
        // Определение региональной ставки в зависимости от выбранного региона
        let regionalRate = 17; // Стандартная региональная часть (17%)
        const selectedRegion = taxRegions.find(region => region.id === formData.taxRegion);
        if (selectedRegion) {
          regionalRate = selectedRegion.regionalRate;
        }
        
        // Применение специальных налоговых ставок для особых режимов
        let effectiveFederalRate = federalRate;
        let effectiveRegionalRate = regionalRate;
        
        if (isITCompany) {
          // Для IT-компаний ставка 0% федеральная часть и 0% региональная до 2024 года
          // С 2024 года - 3% федеральная и 0% региональная
          const taxYear = parseInt(formData.taxationYear, 10);
          if (taxYear >= 2021 && taxYear <= 2024) {
            effectiveFederalRate = 3;
            effectiveRegionalRate = 0;
          }
        }
        
        if (isResidentSpecialZone) {
          // Применение ставки в зависимости от типа ОЭЗ
          const selectedZone = specialZoneTypes.find(zone => zone.id === specialZoneType);
          if (selectedZone && selectedZone.taxRate !== undefined) {
            effectiveRegionalRate = selectedZone.taxRate;
          }
        }
        
        // Суммарная основная ставка налога
        const baseTaxRate = effectiveFederalRate + effectiveRegionalRate;
        
        // Ставка для дивидендов зависит от типа получателя и доли участия
        let dividendTaxRate = 13; // Стандартная ставка для резидентов
        
        if (participantType === 'non-resident') {
          dividendTaxRate = 15; // Ставка для нерезидентов
        } else if (participationShare >= 50 && numDividendsIncome >= 5000000) {
          dividendTaxRate = 0; // Ставка 0% для компаний с долей более 50% и вкладом от 5 млн руб.
        }
        
        // --- Расчет налогооблагаемой базы по основной деятельности ---
        // Общая сумма доходов
        const totalIncome = numRevenue + numOtherIncome;
        
        // Расходы с учетом специальных вычетов
        let totalResearchAndDevelopment = numResearchAndDevelopment;
        // Если расходы на НИОКР, то они могут учитываться с коэффициентом 1.5
        if (totalResearchAndDevelopment > 0) {
          totalResearchAndDevelopment = totalResearchAndDevelopment * 1.5;
        }
        
        let totalExpenses = numExpensesRelated + numExpensesOther + totalResearchAndDevelopment + numInvestmentDeduction;
        
        // Налогооблагаемая прибыль до учета убытков прошлых лет
        let taxableIncomeBeforeLosses = Math.max(0, totalIncome - totalExpenses);
        
        // Применение убытков прошлых лет (не более 50% от налогооблагаемой базы)
        const maxLossesApplicable = taxableIncomeBeforeLosses * 0.5;
        const lossesApplied = Math.min(numLossesCarriedForward, maxLossesApplicable);
        
        // Итоговая налогооблагаемая прибыль по основной деятельности
        const taxableIncome = taxableIncomeBeforeLosses - lossesApplied;
        
        // Расчет налога на прибыль по основной деятельности
        const incomeTaxFederal = taxableIncome * (effectiveFederalRate / 100);
        const incomeTaxRegional = taxableIncome * (effectiveRegionalRate / 100);
        const totalIncomeTax = incomeTaxFederal + incomeTaxRegional;
        
        // --- Расчет налога на дивиденды (если применимо) ---
        let dividendTaxAmount = 0;
        
        if (includeDividends && numDividendsIncome > 0) {
          dividendTaxAmount = numDividendsIncome * (dividendTaxRate / 100);
        }
        
        // --- Расчет авансовых платежей (если применимо) ---
        const includeAdvancePayments = formData.advancePayments;
        const advancePaymentMethod = formData.advancePaymentMethod;
        
        let advancePaymentsSchedule = [];
        
        if (includeAdvancePayments) {
          // Расчет авансовых платежей в зависимости от выбранного метода
          if (advancePaymentMethod === 'quarter') {
            // Квартальные авансовые платежи - по итогам каждого квартала
            // Упрощенно считаем, что прибыль равномерно распределена по году
            const quarterlyProfit = taxableIncome / 4;
            
            for (let quarter = 1; quarter <= 4; quarter++) {
              const quarterlyTax = quarterlyProfit * (baseTaxRate / 100);
              
              advancePaymentsSchedule.push({
                period: `${quarter} квартал`,
                taxableIncome: quarterlyProfit,
                taxAmount: quarterlyTax,
                paymentDate: `До 28 числа месяца, следующего за ${quarter} кварталом`
              });
            }
          } else if (advancePaymentMethod === 'month') {
            // Ежемесячные авансовые платежи исходя из прибыли предыдущего квартала
            // Для простоты расчета в калькуляторе используем равномерное распределение
            const monthlyProfit = taxableIncome / 12;
            
            for (let month = 1; month <= 12; month++) {
              const monthlyTax = monthlyProfit * (baseTaxRate / 100);
              
              advancePaymentsSchedule.push({
                period: `${month} месяц`,
                taxableIncome: monthlyProfit,
                taxAmount: monthlyTax,
                paymentDate: `До 28 числа каждого месяца`
              });
            }
          } else if (advancePaymentMethod === 'actual') {
            // Ежемесячные авансовые платежи исходя из фактической прибыли
            // Для простоты расчета в калькуляторе используем равномерное распределение
            const monthlyProfit = taxableIncome / 12;
            
            for (let month = 1; month <= 12; month++) {
              const monthlyTax = monthlyProfit * (baseTaxRate / 100);
              
              advancePaymentsSchedule.push({
                period: `${month} месяц (факт)`,
                taxableIncome: monthlyProfit,
                taxAmount: monthlyTax,
                paymentDate: `До 28 числа месяца, следующего за отчетным`
              });
            }
          }
        }
        
        // --- Подготовка итоговых результатов ---
        const totalTaxAmount = totalIncomeTax + dividendTaxAmount;
        const effectiveTaxRate = totalIncome > 0 ? (totalTaxAmount / totalIncome) * 100 : 0;
        
        // Итоговый результат для отображения
        const finalResults = {
          taxationYear: formData.taxationYear,
          totalIncome: totalIncome,
          totalExpenses: totalExpenses,
          taxableIncome: taxableIncome,
          
          incomeTaxFederal: incomeTaxFederal,
          incomeTaxRegional: incomeTaxRegional,
          totalIncomeTax: totalIncomeTax,
          
          dividendTaxAmount: dividendTaxAmount,
          totalTaxAmount: totalTaxAmount,
          
          effectiveTaxRate: effectiveTaxRate,
          
          federalRate: effectiveFederalRate,
          regionalRate: effectiveRegionalRate,
          baseTaxRate: baseTaxRate,
          dividendTaxRate: dividendTaxRate,
          
          lossesApplied: lossesApplied,
          specialDeductionsTotal: totalResearchAndDevelopment + numInvestmentDeduction,
          
          // Для отображения подробных сведений
          revenueAmount: numRevenue,
          otherIncomeAmount: numOtherIncome,
          expensesRelatedAmount: numExpensesRelated,
          expensesOtherAmount: numExpensesOther,
          
          researchAndDevelopmentAmount: numResearchAndDevelopment,
          investmentDeductionAmount: numInvestmentDeduction,
          lossesCarriedForwardAmount: numLossesCarriedForward,
          
          dividendsIncomeAmount: numDividendsIncome,
          dividendsExpensesAmount: numDividendsExpenses,
          participationShare: participationShare,
          participantType: participantType,
          
          isITCompany: isITCompany,
          isResidentSpecialZone: isResidentSpecialZone,
          specialZoneType: specialZoneType,
        };
        
        // Детальные расчеты для отображения
        const detailedCalculations = {
          incomeDetails: [
            { name: 'Выручка от реализации', amount: numRevenue },
            { name: 'Внереализационные доходы', amount: numOtherIncome },
            { name: 'Итого доходов', amount: totalIncome, isBold: true }
          ],
          
          expenseDetails: [
            { name: 'Расходы, связанные с производством и реализацией', amount: numExpensesRelated },
            { name: 'Внереализационные расходы', amount: numExpensesOther }
          ],
          
          // Добавляем специальные вычеты, если они применяются
          deductionDetails: [],
          
          taxDetails: [
            { name: 'Налогооблагаемая база', amount: taxableIncome, isBold: true },
            { name: 'Федеральная часть налога (' + effectiveFederalRate + '%)', amount: incomeTaxFederal },
            { name: 'Региональная часть налога (' + effectiveRegionalRate + '%)', amount: incomeTaxRegional },
            { name: 'Налог на прибыль по основной деятельности', amount: totalIncomeTax, isBold: true }
          ],
          
          // Если есть дивиденды, добавляем их в детализацию
          dividendDetails: [],
          
          // Авансовые платежи, если они применяются
          advancePaymentsSchedule: advancePaymentsSchedule
        };
        
        // Добавляем детали по специальным вычетам, если они используются
        if (useSpecialDeductions) {
          if (numResearchAndDevelopment > 0) {
            detailedCalculations.expenseDetails.push(
              { name: 'Расходы на НИОКР (с коэффициентом 1.5)', amount: totalResearchAndDevelopment, highlight: true }
            );
          }
          
          if (numInvestmentDeduction > 0) {
            detailedCalculations.expenseDetails.push(
              { name: 'Инвестиционный вычет', amount: numInvestmentDeduction, highlight: true }
            );
          }
          
          detailedCalculations.expenseDetails.push(
            { name: 'Итого расходов', amount: totalExpenses, isBold: true }
          );
          
          if (numLossesCarriedForward > 0) {
            detailedCalculations.deductionDetails.push(
              { name: 'Убытки прошлых лет (доступно)', amount: numLossesCarriedForward },
              { name: 'Максимально применимо (50% от базы)', amount: maxLossesApplicable },
              { name: 'Применено убытков прошлых лет', amount: lossesApplied, highlight: true }
            );
          }
        } else {
          detailedCalculations.expenseDetails.push(
            { name: 'Итого расходов', amount: totalExpenses, isBold: true }
          );
        }
        
        // Добавляем детали по дивидендам, если они используются
        if (includeDividends && numDividendsIncome > 0) {
          detailedCalculations.dividendDetails = [
            { name: 'Доходы в виде дивидендов', amount: numDividendsIncome },
            { name: 'Ставка налога на дивиденды', amount: dividendTaxRate, isPercent: true },
            { name: 'Налог на дивиденды', amount: dividendTaxAmount, isBold: true }
          ];
          
          detailedCalculations.taxDetails.push(
            { name: 'Налог на дивиденды', amount: dividendTaxAmount },
            { name: 'Итого налог к уплате', amount: totalTaxAmount, isBold: true, isTotal: true }
          );
        } else {
          detailedCalculations.taxDetails.push(
            { name: 'Итого налог к уплате', amount: totalIncomeTax, isBold: true, isTotal: true }
          );
        }
        
        // Устанавливаем результаты
        setResults(finalResults);
        setCalculations(detailedCalculations);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setCalculations(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setCalculations, setIsCalculating, taxRegions, specialZoneTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      revenue: '10000000',
      otherIncome: '500000',
      expensesRelated: '7000000',
      expensesOther: '300000',
      
      taxationYear: new Date().getFullYear().toString(),
      taxRegion: 'default',
      advancePayments: true,
      advancePaymentMethod: 'quarter',
      
      specialDeductions: false,
      researchAndDevelopment: '0',
      investmentDeduction: '0',
      lossesCarriedForward: '0',
      
      specialTaxRegimes: false,
      isITCompany: false,
      isResidentSpecialZone: false,
      specialZoneType: 'none',
      
      dividends: false,
      dividendsIncome: '0',
      dividendsExpenses: '0',
      participantType: 'resident',
      participationShare: '100'
    });
    setResults(null);
    setCalculations(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт расчета в CSV
  const exportToCSV = () => {
    if (!results || !calculations) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Функция форматирования чисел для CSV
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    // Подготовка строк для файла
    const rows = [
      ['Калькулятор налога на прибыль - Результаты расчета'],
      ['Налоговый период:', results.taxationYear],
      [''],
      ['Доходы:'],
      ['Выручка от реализации:', formatCsvNumber(results.revenueAmount)],
      ['Внереализационные доходы:', formatCsvNumber(results.otherIncomeAmount)],
      ['Итого доходов:', formatCsvNumber(results.totalIncome)],
      [''],
      ['Расходы:'],
      ['Расходы, связанные с производством и реализацией:', formatCsvNumber(results.expensesRelatedAmount)],
      ['Внереализационные расходы:', formatCsvNumber(results.expensesOtherAmount)]
    ];
    
    // Добавляем информацию о специальных вычетах, если они применялись
    if (results.specialDeductionsTotal > 0) {
      if (results.researchAndDevelopmentAmount > 0) {
        rows.push(['Расходы на НИОКР (с коэффициентом 1.5):', formatCsvNumber(results.researchAndDevelopmentAmount * 1.5)]);
      }
      if (results.investmentDeductionAmount > 0) {
        rows.push(['Инвестиционный вычет:', formatCsvNumber(results.investmentDeductionAmount)]);
      }
    }
    
    rows.push(['Итого расходов:', formatCsvNumber(results.totalExpenses)]);
    rows.push(['']);
    
    // Добавляем информацию об убытках, если они применялись
    if (results.lossesApplied > 0) {
      rows.push(['Убытки прошлых лет (применено):', formatCsvNumber(results.lossesApplied)]);
    }
    
    rows.push(['Налогооблагаемая база:', formatCsvNumber(results.taxableIncome)]);
    rows.push(['']);
    
    // Информация о налоговых ставках и сумме налога
    rows.push(['Ставка налога на прибыль:', results.baseTaxRate + '%']);
    rows.push(['Федеральная часть (' + results.federalRate + '%):', formatCsvNumber(results.incomeTaxFederal)]);
    rows.push(['Региональная часть (' + results.regionalRate + '%):', formatCsvNumber(results.incomeTaxRegional)]);
    rows.push(['Налог на прибыль по основной деятельности:', formatCsvNumber(results.totalIncomeTax)]);
    
    // Если есть дивиденды, добавляем информацию о них
    if (results.dividendTaxAmount > 0) {
      rows.push(['']);
      rows.push(['Доходы в виде дивидендов:', formatCsvNumber(results.dividendsIncomeAmount)]);
      rows.push(['Ставка налога на дивиденды:', results.dividendTaxRate + '%']);
      rows.push(['Налог на дивиденды:', formatCsvNumber(results.dividendTaxAmount)]);
    }
    
    rows.push(['']);
    rows.push(['Итого налог к уплате:', formatCsvNumber(results.totalTaxAmount)]);
    rows.push(['Эффективная налоговая ставка:', formatCsvNumber(results.effectiveTaxRate) + '%']);
    
    // Если включены авансовые платежи, добавляем график
    if (calculations.advancePaymentsSchedule && calculations.advancePaymentsSchedule.length > 0) {
      rows.push(['']);
      rows.push(['График авансовых платежей:']);
      rows.push(['Период', 'Налогооблагаемая база', 'Сумма налога', 'Срок уплаты']);
      
      calculations.advancePaymentsSchedule.forEach(payment => {
        rows.push([
          payment.period,
          formatCsvNumber(payment.taxableIncome),
          formatCsvNumber(payment.taxAmount),
          payment.paymentDate
        ]);
      });
    }
    
    // Формируем CSV-контент
    const csvContent = '\uFEFF' + rows.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_налога_на_прибыль.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateTax();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <FileText className="w-6 h-6 text-indigo-500" />
          Расчет налога на прибыль организаций
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Секция доходов */}
        <IncomeParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Секция расходов */}
        <ExpenseParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Секция налоговых ставок */}
        <TaxRateParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          taxRegions={taxRegions}
          specialZoneTypes={specialZoneTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          advancePaymentMethods={advancePaymentMethods}
        />

        {/* Секция специальных вычетов */}
        {formData.specialDeductions && (
          <SpecialDeductionsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Секция дивидендов */}
        {formData.dividends && (
          <DividendsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать налог'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;